// @ts-nocheck

import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';

import { Target } from '../../../client';
import { TIMER_INCREMENT, TIMER_DECREMENT, TIMER_STOP } from '../../../constants/timer';
import { __ } from '../../../i18n/l10n';
import PeriodIds, { type PeriodId } from '../../../types/periodIds';

const getPeriodLabelByPeriodId = (periodId) => {
  switch (periodId) {
    case PeriodIds.PERIOD_ID_1ST_QUARTER:
    case PeriodIds.PERIOD_ID_BASKET_1ST_BREAK:
      return 'Q1';
    case PeriodIds.PERIOD_ID_2ND_QUARTER:
    case PeriodIds.PERIOD_ID_BASKET_2ND_BREAK:
      return 'Q2';
    case PeriodIds.PERIOD_ID_3RD_QUARTER:
    case PeriodIds.PERIOD_ID_BASKET_3RD_BREAK:
      return 'Q3';
    case PeriodIds.PERIOD_ID_4TH_QUARTER:
    case PeriodIds.PERIOD_ID_BASKET_4TH_BREAK:
      return 'Q4';
    case PeriodIds.PERIOD_ID_SOCCER_HALF_TIME:
      return __('!short:HalfTime');
    default:
      return;
  }
};

const Period = styled.div`
  text-align: center;
  text-transform: uppercase;
`;

type TimerProps = {
  className?: string;
  type: TIMER_INCREMENT | TIMER_DECREMENT | TIMER_STOP;
  time?: string;
  seconds: number;
  isAdditionalTime?: boolean;
  showOnlyMinutes?: boolean;
  periodId?: PeriodId;
  showPeriod?: boolean;
};

const Timer = ({
  className,
  type,
  time,
  seconds: secondsFromBackend,
  isAdditionalTime = false,
  showOnlyMinutes = false,
  periodId,
  showPeriod,
}: TimerProps) => {
  const from = useRef(moment().unix());
  const [seconds, setSeconds] = useState(secondsFromBackend);

  const period = useMemo(() => (showPeriod ? getPeriodLabelByPeriodId(periodId) : null), [showPeriod, periodId]);

  useEffect(() => {
    from.current = moment().unix();
    setSeconds(secondsFromBackend);
  }, [secondsFromBackend]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = moment().unix();
      const diff = now - from.current;
      let nextSeconds = 0;

      if (type === TIMER_INCREMENT) {
        nextSeconds = secondsFromBackend + diff;
      } else if (type === TIMER_DECREMENT) {
        nextSeconds = secondsFromBackend - diff;
      } else {
        nextSeconds = secondsFromBackend;
      }
      if (nextSeconds >= 0 && (nextSeconds !== secondsFromBackend || type === TIMER_STOP)) {
        setSeconds(nextSeconds);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [secondsFromBackend, type]);

  let minutesToDisplay = Math.floor(seconds / 60);
  minutesToDisplay = minutesToDisplay > 0 ? minutesToDisplay : 0;
  minutesToDisplay = minutesToDisplay < 10 ? `0${minutesToDisplay}` : minutesToDisplay;

  let secondsToDisplay = seconds % 60;
  secondsToDisplay = secondsToDisplay > 0 ? secondsToDisplay : 0;
  secondsToDisplay = secondsToDisplay < 10 ? `0${secondsToDisplay}` : secondsToDisplay;

  const minutesSeconds = showOnlyMinutes ? `${minutesToDisplay}’` : `${minutesToDisplay}:${secondsToDisplay}`;

  return (
    <span className={className}>
      {period ? (
        <Period>{period}</Period>
      ) : (
        <>
          {isAdditionalTime && `${time ?? ''} +${minutesSeconds}`}
          {!isAdditionalTime && minutesSeconds}
        </>
      )}
    </span>
  );
};

if (Target.__ENV__ !== 'production') {
  Timer.propTypes = {
    isAdditionalTime: PropTypes.bool,
    showOnlyMinutes: PropTypes.bool,
    seconds: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    time: PropTypes.string,
  };
}

export default Timer;
